import dayjs from "dayjs";
import { FIXED_MAXIMUM_INVESTMENT_AMOUNT } from "config/envConfig";
import utc from "dayjs/plugin/utc";
import { normalizeProjectStatus } from "utils/projectsList";
import { fileItemUrl } from "utils/uploadFormItems";

dayjs.extend(utc);

function unwrapProjectPayload(payload) {
  const root =
    payload != null && typeof payload === "object" && Object.hasOwn(payload, "data")
      ? payload.data
      : payload ?? {};
  if (root && typeof root === "object") {
    if (root.project && typeof root.project === "object") return root.project;
    if (root.item && typeof root.item === "object") return root.item;
  }
  return root;
}

function formatAmountField(value) {
  if (value === "" || value == null) return "";
  const n = typeof value === "number" ? value : Number(String(value).replaceAll(",", ""));
  if (!Number.isFinite(n)) return String(value).trim();
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function isoOrYmdToCampaignYmd(value) {
  if (!value) return "";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = dayjs(s);
  return d.isValid() ? d.format("YYYY-MM-DD") : s.slice(0, 10);
}

function industryIdFromApi(industry) {
  if (industry == null) return "";
  if (typeof industry === "string" || typeof industry === "number") return String(industry);
  return String(industry.id ?? industry._id ?? "").trim();
}

function imageUrlsToFormItems(urls) {
  return urls
    .map((u) => String(u).trim())
    .filter(Boolean)
    .map((url, i) => ({
      id: `loaded-media-${i}-${url.slice(0, 48)}`,
      file: url,
      status: "complete",
      progress: 100,
    }));
}

function extractMediaUrlsForForm(p) {
  if (Array.isArray(p.imageUrl) && p.imageUrl.length) {
    const asStrings = p.imageUrl.map((x) => (typeof x === "string" ? x : x?.url ?? "")).filter(Boolean);
    return imageUrlsToFormItems(asStrings);
  }
  const fromImages = Array.isArray(p.images) ? p.images.map((x) => String(x).trim()).filter(Boolean) : [];
  const singleCover = String(p.image ?? "").trim();
  const ordered = fromImages.length ? fromImages : (singleCover ? [singleCover] : []);
  return imageUrlsToFormItems(ordered);
}

function pickAmountField(apiValue) {
  if (apiValue == null || String(apiValue).trim() === "") return undefined;
  const formatted = formatAmountField(apiValue);
  return formatted === "" ? undefined : formatted;
}

/**
 * Maps GET project JSON into a partial form patch (merge with `PROJECT_FORM_INITIAL_VALUES` in the provider).
 */
export function mapApiProjectToFormValues(payload) {
  const p = unwrapProjectPayload(payload);
  const maxInvFmt = pickAmountField(p.maximumInvestmentAmount ?? p.maximum_investment_amount);

  const offeringDocuments = Array.isArray(p.offeringDocuments)
    ? p.offeringDocuments.map((d) => ({
        name: String(d?.name ?? "Document").trim() || "Document",
        url: String(d?.url ?? "").trim(),
      }))
    : [];

  return {
    title: String(p.title ?? "").trim(),
    issuerName: String(p.issuerName ?? p.issuer_name ?? "").trim(),
    typeOfEligibleSecurityOffered: String(
      p.typeOfEligibleSecurityOffered ?? p.type_of_eligible_security_offered ?? "",
    ).trim(),
    status: normalizeProjectStatus(p.status, "draft"),
    industry: industryIdFromApi(p.industry) || String(p.industryId ?? p.industry_id ?? "").trim(),
    campaignStartAt: isoOrYmdToCampaignYmd(p.campaignStartAt ?? p.campaign_start_at),
    campaignEndAt: isoOrYmdToCampaignYmd(p.campaignEndAt ?? p.campaign_end_at),
    totalFundsRequired: pickAmountField(p.totalFundsRequired ?? p.total_funds_required) ?? "",
    hardCap: pickAmountField(p.hardCap ?? p.hard_cap) ?? "",
    minimumInvestmentAmount: pickAmountField(p.minimumInvestmentAmount ?? p.minimum_investment_amount) ?? "",
    ...(maxInvFmt == null ? {} : { maximumInvestmentAmount: maxInvFmt }),
    sharePrice: pickAmountField(p.sharePrice ?? p.share_price) ?? "",
    description: String(p.description ?? p.overview ?? "").trim(),
    imageUrl: extractMediaUrlsForForm(p),
    companyWebsite: String(p.companyWebsite ?? p.company_website ?? "").trim(),
    companyHeadOfficeAddress: String(p.companyHeadOfficeAddress ?? p.company_head_office_address ?? "").trim(),
    companyPhone: String(p.companyPhone ?? p.company_phone ?? "").trim(),
    companyEmail: String(p.companyEmail ?? p.company_email ?? "").trim(),
    companyOverview: String(p.companyOverview ?? p.company_overview ?? "").trim(),
    teamMembers: (() => {
      if (Array.isArray(p.teamMembers) && p.teamMembers.length) {
        return p.teamMembers.map((m, i) => ({
          id: String(m.id ?? `loaded-member-${i}`),
          name: String(m.name ?? "").trim(),
          position: String(m.position ?? m.positionAtIssuer ?? "").trim(),
          municipality: String(m.municipality ?? m.municipalityOfResidence ?? "").trim(),
          email: String(m.email ?? "").trim(),
          occupation: String(m.occupation ?? m.principalOccupation ?? "").trim(),
          description: String(m.description ?? m.about ?? "").trim(),
          imageUrl: String(m.imageUrl ?? "").trim(),
          imagePending: "",
        }));
      }
      return [{
        id: "loaded-member-0",
        name: "",
        position: "",
        municipality: "",
        email: "",
        occupation: "",
        description: String(p.teamDescription ?? p.team_description ?? p.teamInfo ?? p.team_info ?? "").trim(),
        imageUrl: String(p.teamImageUrl ?? p.team_image_url ?? "").trim(),
        imagePending: "",
      }];
    })(),
    offeringDocuments,
    newsUpdates: (() => {
      const raw = p.newsUpdates ?? p.news_updates ?? p.newsAndUpdates ?? [];
      if (!Array.isArray(raw) || !raw.length) {
        return [{ id: "news-0", updateTitle: "", date: "", updateDescription: "" }];
      }
      return raw.map((item, i) => ({
        id: String(item.id ?? `loaded-news-${i}`),
        updateTitle: String(item.updateTitle ?? item.title ?? "").trim(),
        date: isoOrYmdToCampaignYmd(item.date ?? item.publishedAt ?? item.published_at),
        updateDescription: String(item.updateDescription ?? item.description ?? item.body ?? "").trim(),
      }));
    })(),
  };

}

function parseAmount(raw) {
  if (raw == null) return Number.NaN;
  const s = String(raw).replaceAll(",", "").trim();
  if (s === "") return Number.NaN;
  const n = Number(s);
  return Number.isFinite(n) ? n : Number.NaN;
}

function ymdToIsoStart(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const d = dayjs(ymd, "YYYY-MM-DD", true);
  return d.isValid() ? d.utc().startOf("day").toISOString() : null;
}

function ymdToIsoEnd(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const d = dayjs(ymd, "YYYY-MM-DD", true);
  return d.isValid() ? d.utc().endOf("day").toISOString() : null;
}

/**
 * POST/PUT body for `/api/admin/projects`.
 * `imageUrl` in form state is an array of upload rows; API receives `imageUrl` as an array of URL strings.
 */
export function buildProjectApiPayload(values) {
  const sharePrice = parseAmount(values.sharePrice);
  const totalFundsRequired = parseAmount(values.totalFundsRequired);
  const hardCap = parseAmount(values.hardCap);
  const minimumInvestmentAmount = parseAmount(values.minimumInvestmentAmount);
  let maximumInvestmentAmount = parseAmount(values.maximumInvestmentAmount);
  if (!Number.isFinite(maximumInvestmentAmount)) {
    maximumInvestmentAmount = parseAmount(FIXED_MAXIMUM_INVESTMENT_AMOUNT);
  }
  if (!Number.isFinite(maximumInvestmentAmount)) maximumInvestmentAmount = 0;

  const imageUrl = (Array.isArray(values.imageUrl) ? values.imageUrl : [])
    .map(fileItemUrl)
    .filter(Boolean);

  const offeringDocuments = (Array.isArray(values.offeringDocuments) ? values.offeringDocuments : [])
    .map((d) => ({
      name: String(d?.name ?? "Document").trim() || "Document",
      url: String(d?.url ?? "").trim(),
    }))
    .filter((doc) => doc.url);

  const teamMembers = (Array.isArray(values.teamMembers) ? values.teamMembers : []).map((m) => ({
    name: String(m.name ?? "").trim(),
    position: String(m.position ?? "").trim(),
    municipalityOfResidence: String(m.municipality ?? "").trim(),
    email: String(m.email ?? "").trim(),
    principalOccupation: String(m.occupation ?? "").trim(),
    about: String(m.description ?? ""),
    imageUrl: String(m.imageUrl ?? "").trim(),
  }));

  const newsUpdates = (Array.isArray(values.newsUpdates) ? values.newsUpdates : []).map((item) => ({
    updateTitle: String(item.updateTitle ?? "").trim(),
    date: ymdToIsoStart(item.date) || "",
    updateDescription: String(item.updateDescription ?? "").trim(),
  }));

  return {
    title: String(values.title ?? "").trim(),
    issuerName: String(values.issuerName ?? "").trim(),
    typeOfEligibleSecurityOffered: String(values.typeOfEligibleSecurityOffered ?? "").trim(),
    description: String(values.description ?? "").trim(),
    status: normalizeProjectStatus(values.status, "draft"),
    industry: String(values.industry ?? "").trim(),
    imageUrl,
    companyWebsite: String(values.companyWebsite ?? "").trim(),
    companyEmail: String(values.companyEmail ?? "").trim(),
    companyPhone: String(values.companyPhone ?? "").trim(),
    companyHeadOfficeAddress: String(values.companyHeadOfficeAddress ?? "").trim(),
    companyOverview: String(values.companyOverview ?? ""),
    companyOverviewImageUrl: "",
    teamMembers,
    offeringDocuments,
    newsUpdates,
    sharePrice: Number.isFinite(sharePrice) ? sharePrice : 0,
    totalFundsRequired: Number.isFinite(totalFundsRequired) ? totalFundsRequired : 0,
    minimumInvestmentAmount: Number.isFinite(minimumInvestmentAmount) ? minimumInvestmentAmount : 0,
    maximumInvestmentAmount,
    hardCap: Number.isFinite(hardCap) ? hardCap : 0,
    campaignStartAt: ymdToIsoStart(values.campaignStartAt) || "",
    campaignEndAt: ymdToIsoEnd(values.campaignEndAt) || "",
  };
}
